import { ReactNode, useContext, useState } from "react"
import { TreeProps } from "./Tree"
import { TreeContext } from "./TreeProvider"
import { SmModalReact } from "../SmModal"
import TreeCell from "./TreeCell"

const LABEL_CLASS = "a-middle-between w-100 tree-hover"
const MATCHED_LABEL_CLASS = `${LABEL_CLASS} border b-orange-important ps-2`

interface TreeLabelProps {
  index: number
  id: number
  selectedId?: number
  children: ReactNode
  can_create?: boolean
  canDelete?: boolean
  canEdit?: boolean
  canCopy?: boolean
  canMove?: boolean
  canSelect?: boolean
  setProps: (props: Partial<TreeProps>) => void
  setExpandParent?: (expand: boolean) => void
  className: string
  extraFields: { [fieldName: string]: number | string | boolean }
}

const TreeLabel = ({
  index,
  id,
  children,
  canDelete,
  canEdit,
  canCopy,
  canMove,
  canSelect,
  setProps,
  className,
  extraFields,
  selectedId,
}: TreeLabelProps) => {
  const { nDelete, enableMultiSelect,  nCopy, nEdit, nMove, columns, moveDirection, matchedIds } =
    useContext(TreeContext)
  let label_class = matchedIds.includes(id) ? MATCHED_LABEL_CLASS : LABEL_CLASS

  if (canSelect) {
    label_class += " clickable"
  }

  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const onSelected = () => {
    if (canSelect) {
      if (enableMultiSelect) {
        let newSelectedId = []
        if (selectedId === undefined) { // Selecting the first item
          newSelectedId = [id]
        } else if(selectedId.includes(id)) { // Deselecting an item
          newSelectedId = selectedId.filter(i => i != id)
        } else { // Selecting additional items
          newSelectedId = [...selectedId, id]
        }
        setProps({selected_id: newSelectedId, entry_index: index})
      } else {
        setProps({selected_id: id, entry_index: index})
      }
    }
  }

  const selectedStyle = {
    backgroundColor: "#d1d1d1",
    borderRadius: "4px",
    padding: "5px 10px",
  }

  return (
    <>
      <div
        className={label_class}
        style={selectedId === id || (enableMultiSelect && selectedId && selectedId.includes(id)) ? selectedStyle : {}}
        onClick={canSelect ? onSelected : undefined}
      >
        <div className="a-middle">
          {canDelete ? (
            <i className="bi bi-trash mx-3 clickable" onClick={() => setShowDeleteModal(true)} />
          ) : null}
          <div className={className}>{children}</div>
        </div>

        <div className="a-middle">
          <div className="tree-auto-hide">
            {canEdit ? (
              <i
                className="bi bi-pencil mx-3 clickable"
                onClick={() => setProps({ n_edit: nEdit + 1, entry_id: id, entry_index: index })}
              />
            ) : null}
            {canCopy ? (
              <i
                className="bi bi-clipboard-plus mx-3 clickable"
                onClick={() => setProps({ n_copy: nCopy + 1, entry_id: id, entry_index: index })}
              />
            ) : null}
            {canMove ? (
              <i
                className={`bi bi-chevron-double-${moveDirection} mx-3 clickable`}
                onClick={() => setProps({ n_move: nMove + 1, entry_id: id, entry_index: index })}
              />
            ) : null}
          </div>
          {columns &&
            Object.entries(columns).map(
              ([col, col_props]) =>
                col in extraFields && (
                  <TreeCell
                    key={col}
                    className={col_props.className + " " + className}
                    type={col_props.type}
                    width={col_props.width}
                    setProps={setProps}
                    field={col}
                    index={index}
                    id={id}
                    value={extraFields[col]}
                  />
                )
            )}
        </div>
      </div>
      {showDeleteModal && (
        <SmModalReact
          is_open={showDeleteModal}
          header="Delete entry"
          setIsOpen={setShowDeleteModal}
          onAccept={() => {
            setProps({ n_delete: nDelete + 1, entry_id: id, entry_index: index, selected_id: [] })
            setShowDeleteModal(false)
          }}
        >
          Are you sure you want to delete entry "{children}"?
        </SmModalReact>
      )}
    </>
  )
}

export default TreeLabel
